import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { AuthProvider } from './context/AuthContext';
import { MasterDataProvider } from './context/MasterDataContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Menu, Loader2 } from 'lucide-react';
// Lazy load pages for performance
const Login = lazy(() => import('./pages/Login'));

const AdvancedDashboard = lazy(() => import('./pages/AdvancedDashboard'));
const EmployeeList = lazy(() => import('./pages/EmployeeList'));
const EmployeeForm = lazy(() => import('./pages/EmployeeForm'));
const TransferForm = lazy(() => import('./pages/TransferForm'));
const AddDocuments = lazy(() => import('./pages/AddDocuments'));
const EmployeeProfile = lazy(() => import('./pages/EmployeeProfile'));
const Reports = lazy(() => import('./pages/Reports'));
const ManageLocations = lazy(() => import('./pages/admin/ManageLocations'));
const ManagePosting = lazy(() => import('./pages/admin/ManagePosting'));
const ManageDesignations = lazy(() => import('./pages/admin/ManageDesignations'));
const ManageQualifications = lazy(() => import('./pages/admin/ManageQualifications'));
const ManageUsers = lazy(() => import('./pages/admin/ManageUsers'));
const FinancialRecords = lazy(() => import('./pages/FinancialRecords'));


// Loading spinner component
const PageLoader = () => (
  <div className="animate-pulse space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-[350px] bg-white rounded-[28px] border border-gray-100 shadow-sm"></div>
      ))}
    </div>
    <div className="h-[400px] bg-white rounded-[32px] border border-gray-100 shadow-sm"></div>
  </div>
);




// MainLayout component to handle Sidebar and Header visibility

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setSidebarOpen] = React.useState(false);
  const userString = localStorage.getItem('hrms_user');
  const user = userString ? JSON.parse(userString) : null;

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-x-hidden">
      <Sidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 lg:ml-64 transition-all duration-300 w-full min-w-0">
        <header className="bg-white shadow-sm sticky top-0 z-30 px-3 sm:px-4 md:px-6 lg:px-8 h-[64px] md:h-[72px] flex justify-between items-center transition-all">

          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 md:p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <Menu size={20} className="md:w-6 md:h-6" />
            </button>
            <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-gray-800 truncate">
              District Judiciary Punjab
            </h2>
          </div>

          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-xs md:text-sm font-medium text-gray-700 truncate max-w-[120px] md:max-w-none">
                {user?.fullName || 'User'}
              </p>
              <p className="text-[10px] md:text-xs text-gray-500 capitalize">{user?.role || 'Access'}</p>
            </div>
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-200 overflow-hidden border border-gray-200 flex-shrink-0">
              <img
                src={user?.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.fullName || 'U')}`}
                alt="Profile"
                width="40"
                height="40"
                className="w-full h-full object-cover"
              />

            </div>
          </div>
        </header>

        <div className="p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1600px] mx-auto w-full">
          {children}
        </div>
      </main>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={
          <Suspense fallback={<PageLoader />}>
            <Login />
          </Suspense>
        } />


        {/* Protected Routes */}
        <Route path="/*" element={
          <ProtectedRoute>
            <MainLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<AdvancedDashboard />} />
                  <Route path="/view-employee/:id" element={<EmployeeProfile />} />

                  {/* Shared Management Routes (Admin + User) */}
                  <Route path="/employees" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><EmployeeList /></ProtectedRoute>
                  } />
                  <Route path="/add-employee" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><EmployeeForm /></ProtectedRoute>
                  } />
                  <Route path="/edit-employee/:id" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><EmployeeForm /></ProtectedRoute>
                  } />
                  <Route path="/transfer/:id" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><TransferForm /></ProtectedRoute>
                  } />
                  <Route path="/documents/:id" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><AddDocuments /></ProtectedRoute>
                  } />
                  <Route path="/financial-records/:id" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'user', 'viewer']}><FinancialRecords /></ProtectedRoute>
                  } />

                  {/* Admin Only Routes */}
                  <Route path="/reports" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><Reports /></ProtectedRoute>
                  } />
                  <Route path="/admin/locations" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><ManageLocations /></ProtectedRoute>
                  } />
                  <Route path="/admin/posting" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><ManagePosting /></ProtectedRoute>
                  } />
                  <Route path="/admin/designations" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><ManageDesignations /></ProtectedRoute>
                  } />
                  <Route path="/admin/qualifications" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><ManageQualifications /></ProtectedRoute>
                  } />
                  <Route path="/admin/users" element={
                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'viewer']}><ManageUsers /></ProtectedRoute>
                  } />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </MainLayout>
          </ProtectedRoute>
        } />
      </Routes>
    </Suspense>
  );
};


const App: React.FC = () => {
  return (
    <AuthProvider>
      <MasterDataProvider>
        <Router>
          <AppRoutes />
        </Router>
      </MasterDataProvider>
    </AuthProvider>
  );
};

export default App;